/** Typed Business Library catalog adapter for Operations and Events.

List and detail pages consume these helpers so JSON Schema parsing lives in
one place. Discovery only — nothing here invokes an Operation or emits an Event.
*/
import { fetchApi } from "@/lib/api";

export const BUSINESS_OPERATIONS_QUERY_KEY = ["business-operations"] as const;
export const BUSINESS_EVENTS_QUERY_KEY = ["business-events"] as const;
export const CATALOG_PAGE_LIMIT = 100;

export type JsonSchema = {
  type?: string | string[];
  title?: string;
  description?: string;
  format?: string;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  items?: JsonSchema;
  additionalProperties?: boolean;
  minLength?: number;
  enum?: unknown[];
  $ref?: string;
  anyOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  "x-business-type"?: string;
};

export type CatalogField = {
  name: string;
  typeLabel: string;
  typeName: string | null;
  required: boolean;
  description: string;
};

/** sop = an SOP step's verb: catalog-only, never invocable by an App. */
export type ImplementationKind = "endpoint" | "automator" | "playwrighter" | "sop";
/**
 * propose = only our own proposal/draft/brief rows; write = HickoryOS state;
 * external = reaches a customer or outside system and needs approval.
 */
export type OperationEffectClass = "propose" | "write" | "external";
export type CatalogStatus = "active" | "retired";

export type BusinessOperationListItem = {
  slug: string;
  name: string;
  description: string;
  active_version: number | null;
  inputs_schema: JsonSchema;
  returns_schema: JsonSchema;
  required_capabilities: string[];
  scope_contract: Record<string, unknown>;
  implementation_kind: ImplementationKind;
  effect_class: OperationEffectClass;
  status: CatalogStatus;
};

export type AuthorizedApp = {
  id: string;
  name: string;
};

export type BusinessOperationDetail = BusinessOperationListItem & {
  source_receipt: Record<string, string>;
  type_references: string[];
  authorized_consumers: AuthorizedApp[];
};

export type BusinessEventListItem = {
  slug: string;
  name: string;
  description: string;
  schema_version: number;
  inputs_schema: JsonSchema;
  required_capabilities: string[];
  scope_contract: Record<string, unknown>;
  producer_key: string;
  transition_description: string;
  status: string;
};

export type BusinessEventParameter = {
  name: string;
  type: string;
  type_kind: "primitive" | "entity_ref";
  required: boolean;
  description: string;
};

export type BusinessEventDetail = BusinessEventListItem & {
  type_references: { name: string; kind: string }[];
  source_receipt: Record<string, string>;
  replay_policy: Record<string, string>;
  authorized_subscribers: AuthorizedApp[];
  transition_key: string;
  parameters: BusinessEventParameter[];
};

export type CatalogPage<T> = {
  items: T[];
  next_cursor: string | null;
};

export const OPERATION_EFFECT_CLASS_LABEL: Record<OperationEffectClass, string> = {
  propose: "Proposes",
  write: "Writes",
  external: "External · Needs approval",
};

export const IMPLEMENTATION_KIND_LABEL: Record<ImplementationKind, string> = {
  endpoint: "Endpoint",
  automator: "Automator",
  playwrighter: "Playwrighter",
  sop: "SOP step",
};

const PRIMITIVE_TYPE_LABEL: Record<string, string> = {
  integer: "int",
  number: "decimal",
  string: "string",
  boolean: "boolean",
  object: "record",
  array: "list",
};

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function asSchema(value: unknown): JsonSchema {
  return asRecord(value) as JsonSchema;
}

function asStringMap(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, item] of Object.entries(asRecord(value))) {
    if (typeof item === "string") out[key] = item;
  }
  return out;
}

function asAuthorizedApps(value: unknown): AuthorizedApp[] {
  if (!Array.isArray(value)) return [];
  const apps: AuthorizedApp[] = [];
  for (const item of value) {
    const row = asRecord(item);
    const id = asString(row.app_id);
    const name = asString(row.app_name);
    if (id && name) apps.push({ id, name });
  }
  return apps;
}

function catalogQuery(
  path: string,
  params: { q?: string; cursor?: string; limit?: number } = {},
): string {
  const search = new URLSearchParams();
  const term = params.q?.trim();
  if (term) search.set("q", term);
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

function typeFromSchema(schema: JsonSchema): { typeLabel: string; typeName: string | null } {
  const businessType = schema["x-business-type"];
  if (businessType) {
    const name = businessType.replace(/^@/, "");
    return {
      typeLabel: businessType.startsWith("@") ? businessType : `@${businessType}`,
      typeName: name,
    };
  }
  if (schema.format === "date") return { typeLabel: "date", typeName: "date" };
  if (schema.format === "date-time") return { typeLabel: "datetime", typeName: "datetime" };
  if (schema.format === "uuid") return { typeLabel: "uuid", typeName: "uuid" };
  const rawType = Array.isArray(schema.type)
    ? schema.type.find((item) => item !== "null")
    : schema.type;
  if (rawType === "array" && schema.items) {
    const inner = typeFromSchema(schema.items);
    return { typeLabel: `${inner.typeLabel}[]`, typeName: inner.typeName };
  }
  if (rawType && PRIMITIVE_TYPE_LABEL[rawType]) {
    const label = PRIMITIVE_TYPE_LABEL[rawType];
    return {
      typeLabel: label,
      typeName: label === "record" || label === "list" ? null : label,
    };
  }
  if (schema.$ref) {
    const name = schema.$ref.split("/").pop() ?? schema.$ref;
    return { typeLabel: name, typeName: name };
  }
  if (schema.title) return { typeLabel: schema.title, typeName: null };
  return { typeLabel: "unknown", typeName: null };
}

/** Concrete parameter/return fields from a JSON Schema object. */
export function fieldsFromJsonSchema(schema: JsonSchema | null | undefined): CatalogField[] {
  const properties = schema?.properties;
  if (!properties) return [];
  const required = new Set(schema.required ?? []);
  return Object.entries(properties).map(([name, property]) => {
    const { typeLabel, typeName } = typeFromSchema(property);
    return {
      name,
      typeLabel,
      typeName,
      required: required.has(name),
      description: property.description ?? "",
    };
  });
}

export function formatCatalogSignature(
  slug: string,
  inputs: JsonSchema | null | undefined,
  returns?: JsonSchema | null,
): string {
  const params = fieldsFromJsonSchema(inputs)
    .map((field) => `${field.name}: ${field.typeLabel}${field.required ? "" : "?"}`)
    .join(", ");
  if (returns === undefined) return `${slug}(${params})`;
  const returnFields = fieldsFromJsonSchema(returns);
  const returnLabel =
    returns?.title ??
    (returnFields.length === 1 ? returnFields[0].typeLabel : (returns?.type ?? "object"));
  return `${slug}(${params}) -> ${returnLabel}`;
}

function parseImplementationKind(value: unknown): ImplementationKind {
  return value === "automator" || value === "playwrighter" || value === "endpoint" || value === "sop"
    ? value
    : "endpoint";
}

function parseOperationEffectClass(value: unknown): OperationEffectClass {
  // An unknown value reads as the most cautious class, never as harmless.
  return value === "propose" || value === "write" ? value : "external";
}

function parseCatalogStatus(value: unknown): CatalogStatus {
  return value === "retired" ? "retired" : "active";
}

export function parseOperationListItem(raw: unknown): BusinessOperationListItem {
  const row = asRecord(raw);
  return {
    slug: asString(row.slug),
    name: asString(row.name),
    description: asString(row.description),
    active_version: asNumber(row.active_version),
    inputs_schema: asSchema(row.inputs_schema),
    returns_schema: asSchema(row.returns_schema),
    required_capabilities: asStringList(row.required_capabilities),
    scope_contract: asRecord(row.scope_contract),
    implementation_kind: parseImplementationKind(row.implementation_kind),
    effect_class: parseOperationEffectClass(row.effect_class),
    status: parseCatalogStatus(row.status),
  };
}

export function parseOperationDetail(raw: unknown): BusinessOperationDetail {
  const row = asRecord(raw);
  const item = parseOperationListItem(row);
  return {
    ...item,
    source_receipt: asStringMap(row.source_receipt),
    type_references: asStringList(row.type_references),
    authorized_consumers: asAuthorizedApps(row.authorized_consumers),
  };
}

export function parseEventListItem(raw: unknown): BusinessEventListItem {
  const row = asRecord(raw);
  return {
    slug: asString(row.slug),
    name: asString(row.name),
    description: asString(row.description),
    schema_version: asNumber(row.schema_version) ?? 1,
    inputs_schema: asSchema(row.inputs_schema),
    required_capabilities: asStringList(row.required_capabilities),
    scope_contract: asRecord(row.scope_contract),
    producer_key: asString(row.producer_key),
    transition_description: asString(row.transition_description),
    status: asString(row.status, "active"),
  };
}

function parseEventParameters(raw: unknown, inputs: JsonSchema): BusinessEventParameter[] {
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((item) => {
      const row = asRecord(item);
      return {
        name: asString(row.name),
        type: asString(row.type, "string"),
        type_kind: row.type_kind === "entity_ref" ? "entity_ref" : "primitive",
        required: row.required !== false,
        description: asString(row.description),
      };
    });
  }
  return fieldsFromJsonSchema(inputs).map((field) => ({
    name: field.name,
    type: field.typeLabel,
    type_kind: "primitive" as const,
    required: field.required,
    description: field.description,
  }));
}

export function parseEventDetail(raw: unknown): BusinessEventDetail {
  const row = asRecord(raw);
  const item = parseEventListItem(row);
  const typeReferences = Array.isArray(row.type_references)
    ? row.type_references.map((item) => {
        const ref = asRecord(item);
        return { name: asString(ref.name), kind: asString(ref.kind, "primitive") };
      })
    : [];
  return {
    ...item,
    type_references: typeReferences,
    source_receipt: asStringMap(row.source_receipt),
    replay_policy: asStringMap(row.replay_policy),
    authorized_subscribers: asAuthorizedApps(row.authorized_subscribers),
    transition_key: asString(row.transition_key),
    parameters: parseEventParameters(row.parameters, item.inputs_schema),
  };
}

function parseCatalogPage<T>(raw: unknown, parseItem: (item: unknown) => T): CatalogPage<T> {
  const row = asRecord(raw);
  const items = Array.isArray(row.items) ? row.items.map(parseItem) : [];
  return {
    items,
    next_cursor: typeof row.next_cursor === "string" ? row.next_cursor : null,
  };
}

export async function listBusinessOperations(params: { q?: string; cursor?: string } = {}) {
  const raw = await fetchApi(
    catalogQuery("/business-operations", { ...params, limit: CATALOG_PAGE_LIMIT }),
  );
  return parseCatalogPage(raw, parseOperationListItem);
}

export async function getBusinessOperation(slug: string) {
  return parseOperationDetail(await fetchApi(`/business-operations/${slug}`));
}

export async function listBusinessEvents(params: { q?: string; cursor?: string } = {}) {
  const raw = await fetchApi(
    catalogQuery("/business-events", { ...params, limit: CATALOG_PAGE_LIMIT }),
  );
  return parseCatalogPage(raw, parseEventListItem);
}

export async function getBusinessEvent(slug: string) {
  return parseEventDetail(await fetchApi(`/business-events/${slug}`));
}

/** The Business Library's sections, in overview order; each has its own list page. */
export const LIBRARY_SECTION_ORDER = ["objects", "definitions", "operations", "events"] as const;
export type LibrarySectionKey = (typeof LIBRARY_SECTION_ORDER)[number];

export function typeHref(typeName: string | null): string | null {
  if (!typeName) return null;
  return `/business-library/objects/${typeName.replace(/^@/, "")}`;
}

export function librarySectionHref(
  section: "operations" | "events",
  search = "",
): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return `/business-library/${section}${params.toString() ? `?${params.toString()}` : ""}`;
}
